import * as React from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { Spinner, Icon, OverlayTrigger } from "components/common";
import EditableTitle, { Title } from "views/components/common/EditableTitle";
import { Input } from "components/bootstrap";
import IconButton from "components/common/IconButton";
import { widgetDragHandleClass } from "views/components/widgets/Constants";
import { useIsInteractiveMode } from "views/components/contexts/InteractiveContext";
const LoadingSpinner = styled(Spinner)`
  margin-left: 10px;
`;
const Container = styled.div(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.large};
    text-overflow: ellipsis;
    margin-bottom: 5px;
    display: grid;
    grid-template-columns: minmax(35px, 1fr) max-content;
    align-items: center;
    min-height: 25px;
    position: relative; // required for absolute positioned widget actions

    .widget-title {
      width: 100%;
      max-width: 400px;
    }
  `
);
const Col = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const DragHandleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const WidgetDragHandle = styled(Icon)`
  cursor: move;
  opacity: 0.3;
  margin-right: 5px;
`;
const TitleInputWrapper = styled.div`
  max-width: 400px;
  width: 100%;

  .form-group {
    margin-bottom: 5px;
    width: 100%;
  }
`;
const TitleInput = styled(Input)(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.large};
    width: 100%;
  `
);
const WidgetTitle = ({ onChange = void 0, editing, title, titleIcon = void 0 }) => {
  if (typeof onChange !== "function") {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Title, null, title), titleIcon);
  }
  return editing ? /* @__PURE__ */ React.createElement(TitleInputWrapper, null, /* @__PURE__ */ React.createElement(
    TitleInput,
    {
      type: "text",
      id: "widget-title",
      onChange: (e) => onChange(e.target.value),
      defaultValue: title,
      required: true
    }
  )) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(EditableTitle, { key: title, disabled: !onChange, value: title, onChange }), titleIcon);
};
const DescriptionInputWrapper = styled.div`
  flex-grow: 1;
  width: 100%;

  .form-group {
    margin-bottom: 5px;
    width: 100%;
  }
`;
const DescriptionInput = styled(Input)(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.large};
    width: 100%;
  `
);
const DescriptionPopover = ({ description }) => {
  const { t } = useTranslation("widgets");
  return /* @__PURE__ */ React.createElement(
    OverlayTrigger,
    {
      trigger: "click",
      rootClose: true,
      placement: "bottom",
      overlay: description != null ? description : /* @__PURE__ */ React.createElement("i", null, t("widget_header.no_description"))
    },
    /* @__PURE__ */ React.createElement(IconButton, { title: t("widget_header.show_description"), name: "help" })
  );
};
const WidgetDescription = ({ onChange = void 0, editing, description }) => {
  const { t } = useTranslation("widgets");
  if (typeof onChange !== "function") {
    return /* @__PURE__ */ React.createElement(Title, null, description);
  }
  if (!editing) {
    return description ? /* @__PURE__ */ React.createElement(DescriptionPopover, { description }) : null;
  }
  return /* @__PURE__ */ React.createElement(DescriptionInputWrapper, null, /* @__PURE__ */ React.createElement(
    DescriptionInput,
    {
      type: "text",
      id: "widget-description",
      placeholder: t("widget_header.description_placeholder"),
      onChange: (e) => onChange(e.target.value),
      defaultValue: description,
      required: true
    }
  ));
};
const WidgetHeader = ({
  title,
  description,
  editing,
  hideDragHandle = false,
  loading = false,
  children = void 0,
  titleIcon = void 0,
  onRename = void 0,
  onUpdateDescription = void 0
}) => {
  const { t } = useTranslation("widgets");
  const interactive = useIsInteractiveMode();
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(Col, null, hideDragHandle || /* @__PURE__ */ React.createElement(DragHandleContainer, { className: widgetDragHandleClass, title: t("widget_header.drag_handle", { title }) }, /* @__PURE__ */ React.createElement(WidgetDragHandle, { name: "drag_indicator" })), /* @__PURE__ */ React.createElement(
    WidgetTitle,
    {
      editing,
      title,
      titleIcon,
      onChange: interactive ? onRename : void 0
    }
  ), interactive && /* @__PURE__ */ React.createElement(WidgetDescription, { editing, description, onChange: onUpdateDescription }), loading && /* @__PURE__ */ React.createElement(LoadingSpinner, { text: "", delay: 0 })), children);
};
export default WidgetHeader;
